import Edit from '@mui/icons-material/Edit';
import { Button, Typography, TypographyProps } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { PANEL } from '@/contexts/UIState/types';
import { UIStateContext } from '@/contexts/UIState/context';
import { useUserRoles } from '@/service/hooks/useUserRoles';
import { useAuthToken } from '@/contexts/auth/useAuthToken';
import {
  CollectionStatusId,
  COLLECTION_STATUS,
  FixtureStatusId,
  FIXTURE_STATUS,
  USER_ROLE_ID,
  COLLECTION_STATUS_ID,
} from '@/service/constants';
import { COVERAGE_LEVEL_OPTIONS } from '@/components/StatusesEditDialog/constants';
import { InfoLabel } from '../common/InfoLabel';
import { TextSeparator } from '../common/TextSeparator';
import { StatusesEditDialog } from '../StatusesEditDialog/StatusesEditDialog';

const StatusText: FC<TypographyProps> = (props) => {
  return (
    <Typography
      fontSize='inherit'
      component='span'
      variant='body2'
      lineHeight={1}
      {...props}
    />
  );
};
const StatusValueText: FC<TypographyProps> = (props) => {
  return <StatusText fontWeight={700} {...props} />;
};

export interface StatusesBarProps {
  fixtureStatusId?: FixtureStatusId;
  collectionStatusId?: CollectionStatusId;
  coverageLevelId?: number;
}

const EDIT_USER_ROLES = [USER_ROLE_ID.SUPER_ADMIN, USER_ROLE_ID.SUPERVISOR];

const StatusBarContent: FC<StatusesBarProps & { canEdit: boolean }> = ({
  fixtureStatusId,
  collectionStatusId,
  coverageLevelId,
  canEdit,
}) => {
  const isSuspendedCollectionStatus = useMemo(
    () => collectionStatusId === COLLECTION_STATUS_ID.SUSPENDED,
    [collectionStatusId]
  );

  return (
    <InfoLabel>
      <StatusText>
        {'Fixture Status: '}
        <StatusValueText>
          {fixtureStatusId !== undefined && FIXTURE_STATUS[fixtureStatusId]}
        </StatusValueText>
      </StatusText>

      <TextSeparator />

      <StatusText>
        {'Collection Status: '}
        <StatusValueText
          sx={{
            animation: isSuspendedCollectionStatus
              ? 'warning-animation-blink 1s infinite'
              : 'none',
            padding: (theme) =>
              isSuspendedCollectionStatus ? theme.spacing(0.25, 0.5) : 0,
            borderRadius: (theme) =>
              isSuspendedCollectionStatus ? theme.spacing(0.5) : 0,
          }}
        >
          {collectionStatusId !== undefined &&
            COLLECTION_STATUS[collectionStatusId]}
        </StatusValueText>
      </StatusText>

      <TextSeparator />

      <StatusText>
        {'Coverage Level: '}
        <StatusValueText>
          {
            COVERAGE_LEVEL_OPTIONS.find(
              (coverageLevelOption) =>
                coverageLevelOption.value === coverageLevelId
            )?.name
          }
        </StatusValueText>
      </StatusText>
      {canEdit && <Edit fontSize='inherit' />}
    </InfoLabel>
  );
};

export const StatusesBar: FC<StatusesBarProps> = (props) => {
  const { openedPanel, setOpenedPanel } = useContext(UIStateContext);
  const token = useAuthToken();
  const { data: roles } = useUserRoles({ token });
  const canEdit = roles
    ? roles.some(({ id }) => EDIT_USER_ROLES.includes(id))
    : false;

  if (canEdit) {
    return (
      <>
        <Button
          color='secondary'
          variant='contained'
          disableFocusRipple
          sx={{ textTransform: 'unset', p: 0 }}
          onClick={() => setOpenedPanel(PANEL.STATUSES)}
        >
          <StatusBarContent {...props} canEdit={canEdit} />
        </Button>
        <StatusesEditDialog
          open={openedPanel === PANEL.STATUSES}
          onClose={() => setOpenedPanel(null)}
        />
      </>
    );
  }

  return <StatusBarContent {...props} canEdit={canEdit} />;
};
